import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Pagination,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ShoppingCart,
  Sell,
  CheckCircle,
  Cancel,
  Schedule,
  Refresh,
  HourglassEmpty,
  Error,
} from '@mui/icons-material';
import axios from 'axios';
import MobileStockCard from '../components/shared/MobileStockCard';

const Orders = () => {
  const navigate = useNavigate();
  const [allOrders, setAllOrders] = useState([]);
  const [ordersSource, setOrdersSource] = useState('database');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const ordersPerPage = 20;

  useEffect(() => {
    fetchOrders();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sideFilter]);

  const fetchOrders = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await axios.get('/trading/orders?limit=500');

      if (response.data.orders) {
        setAllOrders(response.data.orders);
        setOrdersSource(response.data.source);
      } else {
        setAllOrders(response.data);
        setOrdersSource('database');
      }
    } catch (err) {
      setError('Failed to fetch orders. Please check your connection and try again.');
      console.error('Orders fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Compute summary counts from all orders
  const summary = useMemo(() => {
    const counts = { total: allOrders.length, open: 0, completed: 0, cancelled: 0, buy: 0, sell: 0 };
    for (const o of allOrders) {
      const s = (o.status || '').toUpperCase();
      if (s === 'OPEN' || s === 'PENDING' || s === 'NEW' || s === 'ACCEPTED') counts.open++;
      else if (s === 'COMPLETED' || s === 'FILLED') counts.completed++;
      else if (s === 'CANCELLED' || s === 'CANCELED' || s === 'EXPIRED') counts.cancelled++;
      const side = (o.order_type || o.side || '').toUpperCase();
      if (side === 'BUY') counts.buy++;
      else if (side === 'SELL') counts.sell++;
    }
    return counts;
  }, [allOrders]);

  // Filter orders based on selected filters
  const filteredOrders = useMemo(() => {
    return allOrders.filter((o) => {
      // Status filter
      if (statusFilter !== 'all') {
        const s = (o.status || '').toUpperCase();
        if (statusFilter === 'open' && s !== 'OPEN' && s !== 'PENDING' && s !== 'NEW' && s !== 'ACCEPTED') return false;
        if (statusFilter === 'completed' && s !== 'COMPLETED' && s !== 'FILLED') return false;
        if (statusFilter === 'cancelled' && s !== 'CANCELLED' && s !== 'CANCELED' && s !== 'EXPIRED') return false;
      }
      // Side filter
      if (sideFilter !== 'all') {
        const side = (o.order_type || o.side || '').toUpperCase();
        if (sideFilter === 'buy' && side !== 'BUY') return false;
        if (sideFilter === 'sell' && side !== 'SELL') return false;
      }
      return true;
    });
  }, [allOrders, statusFilter, sideFilter]);

  // Paginate filtered orders
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const paginatedOrders = filteredOrders.slice((page - 1) * ordersPerPage, page * ordersPerPage);

  const handleRefresh = () => {
    fetchOrders(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getOrderTypeIcon = (orderType) => {
    return orderType === 'BUY' ? <ShoppingCart fontSize="small" /> : <Sell fontSize="small" />;
  };

  const getOrderTypeColor = (orderType) => {
    return orderType === 'BUY' ? 'primary' : 'secondary';
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
      case 'FILLED':
        return <CheckCircle fontSize="small" />;
      case 'CANCELLED':
      case 'CANCELED':
      case 'EXPIRED':
        return <Cancel fontSize="small" />;
      case 'PENDING':
      case 'ACCEPTED':
      case 'NEW':
      case 'OPEN':
        return <Schedule fontSize="small" />;
      case 'PARTIAL':
      case 'PARTIALLY_FILLED':
        return <HourglassEmpty fontSize="small" />;
      case 'FAILED':
      case 'REJECTED':
        return <Error fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
      case 'FILLED':
        return 'success';
      case 'CANCELLED':
      case 'CANCELED':
      case 'EXPIRED':
        return 'error';
      case 'PENDING':
      case 'ACCEPTED':
      case 'NEW':
      case 'OPEN':
        return 'warning';
      case 'PARTIAL':
      case 'PARTIALLY_FILLED':
        return 'info';
      case 'FAILED':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatOrderDetail = (detail) => {
    if (!detail) return 'Market';
    return detail
      .replace('trailing_stop', 'Trailing Stop')
      .replace('market', 'Market')
      .replace('limit', 'Limit')
      .replace('stop', 'Stop');
  };

  if (loading && allOrders.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Skeleton variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rounded" height={80} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1">Order History</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh orders">
            <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Chip
            label={ordersSource === 'alpaca' ? 'Alpaca Live' : 'Database'}
            color={ordersSource === 'alpaca' ? 'success' : 'default'}
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined" sx={{ cursor: 'pointer', border: statusFilter === 'all' ? 2 : 1, borderColor: statusFilter === 'all' ? 'primary.main' : 'divider' }} onClick={() => setStatusFilter('all')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="textSecondary">Total</Typography>
              <Typography variant="h5" fontWeight="bold">{summary.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined" sx={{ cursor: 'pointer', border: statusFilter === 'open' ? 2 : 1, borderColor: statusFilter === 'open' ? 'warning.main' : 'divider' }} onClick={() => setStatusFilter(statusFilter === 'open' ? 'all' : 'open')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="textSecondary">Open</Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">{summary.open}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined" sx={{ cursor: 'pointer', border: statusFilter === 'completed' ? 2 : 1, borderColor: statusFilter === 'completed' ? 'success.main' : 'divider' }} onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="textSecondary">Completed</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">{summary.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined" sx={{ cursor: 'pointer', border: statusFilter === 'cancelled' ? 2 : 1, borderColor: statusFilter === 'cancelled' ? 'error.main' : 'divider' }} onClick={() => setStatusFilter(statusFilter === 'cancelled' ? 'all' : 'cancelled')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="textSecondary">Cancelled</Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">{summary.cancelled}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined" sx={{ cursor: 'pointer', border: sideFilter === 'buy' ? 2 : 1, borderColor: sideFilter === 'buy' ? 'primary.main' : 'divider' }} onClick={() => setSideFilter(sideFilter === 'buy' ? 'all' : 'buy')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="textSecondary">Buys</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">{summary.buy}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card variant="outlined" sx={{ cursor: 'pointer', border: sideFilter === 'sell' ? 2 : 1, borderColor: sideFilter === 'sell' ? 'secondary.main' : 'divider' }} onClick={() => setSideFilter(sideFilter === 'sell' ? 'all' : 'sell')}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="textSecondary">Sells</Typography>
              <Typography variant="h5" fontWeight="bold" color="secondary.main">{summary.sell}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="textSecondary">Status:</Typography>
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, val) => { if (val) setStatusFilter(val); }}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="open">Open</ToggleButton>
                <ToggleButton value="completed">Completed</ToggleButton>
                <ToggleButton value="cancelled">Cancelled</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="textSecondary">Side:</Typography>
              <ToggleButtonGroup
                value={sideFilter}
                exclusive
                onChange={(e, val) => { if (val) setSideFilter(val); }}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="buy">Buy</ToggleButton>
                <ToggleButton value="sell">Sell</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
              Showing {filteredOrders.length} of {summary.total} orders
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No orders match the current filters
              </Typography>
              <Button variant="outlined" onClick={() => { setStatusFilter('all'); setSideFilter('all'); }} sx={{ mt: 1 }}>
                Clear Filters
              </Button>
            </Box>
          ) : (
            <>
              {/* Mobile: Card layout */}
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {paginatedOrders.map((order) => (
                    <MobileStockCard
                      key={order.id}
                      symbol={order.symbol}
                      name={order.company_name}
                      orderType={order.order_type}
                      orderStatus={order.status}
                      orderDate={order.order_date}
                      totalAmount={parseFloat(order.total_amount)}
                      shares={order.shares}
                      currentPrice={parseFloat(order.price)}
                      onClick={() => navigate(`/stock/${order.symbol}`)}
                    />
                  ))}
                </Box>
              ) : (
              /* Desktop: Table layout */
              <TableContainer component={Paper} elevation={0}>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Symbol</strong></TableCell>
                      {!isMobile && <TableCell><strong>Company</strong></TableCell>}
                      <TableCell align="center"><strong>Side</strong></TableCell>
                      <TableCell align="center"><strong>Order Type</strong></TableCell>
                      <TableCell align="right"><strong>Qty</strong></TableCell>
                      <TableCell align="right"><strong>Price</strong></TableCell>
                      {!isMobile && <TableCell align="right"><strong>Filled</strong></TableCell>}
                      <TableCell align="right"><strong>Total</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedOrders.map((order) => {
                      const side = order.order_type || order.side?.toUpperCase();
                      const status = order.status?.toUpperCase();
                      return (
                        <TableRow key={order.id} hover sx={status === 'OPEN' ? { backgroundColor: 'action.hover' } : {}}>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(order.order_date || order.submittedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="primary"
                              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              onClick={() => navigate('/stock/' + order.symbol)}
                            >
                              {order.symbol}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2" noWrap>{order.company_name || order.symbol}</Typography>
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <Chip
                              icon={getOrderTypeIcon(side)}
                              label={side}
                              color={getOrderTypeColor(side)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption" color="textSecondary">
                              {formatOrderDetail(order.order_type_detail)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {parseFloat(order.shares || order.qty || 0).toFixed(order.shares % 1 !== 0 ? 4 : 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {order.filledAvgPrice
                                ? formatCurrency(order.filledAvgPrice)
                                : order.price && parseFloat(order.price) > 0
                                  ? formatCurrency(order.price)
                                  : order.limitPrice
                                    ? formatCurrency(order.limitPrice)
                                    : '—'}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell align="right">
                              <Typography variant="body2">
                                {order.filled_qty !== undefined && order.filled_qty !== null
                                  ? `${parseFloat(order.filled_qty).toFixed(0)} / ${parseFloat(order.shares || order.qty || 0).toFixed(0)}`
                                  : parseFloat(order.shares || order.qty || 0).toFixed(0)}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={side === 'BUY' ? 'error.main' : 'success.main'}
                            >
                              {order.total_amount && parseFloat(order.total_amount) > 0
                                ? `${side === 'BUY' ? '-' : '+'}${formatCurrency(order.total_amount)}`
                                : order.filledAvgPrice && order.filledQty
                                  ? `${side === 'BUY' ? '-' : '+'}${formatCurrency(order.filledAvgPrice * order.filledQty)}`
                                  : order.price && parseFloat(order.price) > 0 && (order.shares || order.qty)
                                    ? `${side === 'BUY' ? '-' : '+'}${formatCurrency(order.price * (order.shares || order.qty))}`
                                    : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              icon={getStatusIcon(order.status)}
                              label={order.status?.toUpperCase() || 'UNKNOWN'}
                              color={getStatusColor(order.status)}
                              size="small"
                              variant="filled"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Orders;
